import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, share } from 'rxjs/operators';

/**
 * CacheEntry 介面定義快取項目的結構
 */
export interface CacheEntry {
  url: string;
  response: HttpResponse<any>;
  timestamp: number;
  expiresIn: number; // 過期時間 (毫秒)
}

/**
 * CacheInterceptor
 *
 * HTTP 請求快取攔截器，負責：
 * 1. 快取 GET 請求的回應
 * 2. 根據快取策略決定是否使用快取
 * 3. 管理快取過期和清理
 * 4. 優化應用程式效能
 */
@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  // 使用 Map 儲存快取，key 為請求 URL，value 為快取項目
  private cache = new Map<string, CacheEntry>();

  // 預設快取過期時間 (5 分鐘)
  private defaultCacheTime = 5 * 60 * 1000;

  // 最大快取項目數量
  private maxCacheSize = 100;

  // 最後一次清理時間
  private lastCleanup = Date.now();

  // 清理間隔 (10 分鐘)
  private cleanupInterval = 10 * 60 * 1000;

  constructor() {
    // 定期清理過期快取
    setInterval(() => this.cleanExpiredCache(), this.cleanupInterval);
  }

  /**
   * 攔截 HTTP 請求並處理快取
   */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // 只快取 GET 請求
    if (request.method !== 'GET') {
      return next.handle(request);
    }

    // 檢查請求是否可快取
    if (!this.isCacheable(request)) {
      return next.handle(request);
    }

    // 檢查是否有有效的快取
    const cachedResponse = this.getFromCache(request.url);
    if (cachedResponse) {
      // 返回快取的回應
      return of(cachedResponse);
    }

    // 如果沒有快取或快取已過期，發送請求並快取回應
    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.addToCache(request.url, event);
        }
      }),
      // 使用 share 運算符確保多個訂閱者共享同一個 HTTP 請求
      share()
    );
  }

  /**
   * 判斷請求是否可快取
   */
  private isCacheable(request: HttpRequest<any>): boolean {
    // 檢查請求標頭是否包含不快取的指示
    if (request.headers.get('Cache-Control') === 'no-cache' ||
        request.headers.get('Pragma') === 'no-cache') {
      return false;
    }

    // 檢查 URL 是否包含不應快取的路徑
    const url = request.url.toLowerCase();

    // 不快取認證相關請求
    if (url.includes('/auth/') || url.includes('/login') || url.includes('/logout')) {
      return false;
    }

    // 不快取包含查詢參數 noCache=true 的請求
    if (url.includes('nocache=true')) {
      return false;
    }

    return true;
  }

  /**
   * 從快取中獲取回應
   */
  private getFromCache(url: string): HttpResponse<any> | null {
    const entry = this.cache.get(url);

    // 如果沒有快取項目，返回 null
    if (!entry) {
      return null;
    }

    // 檢查快取是否過期
    const now = Date.now();
    if (now > entry.timestamp + entry.expiresIn) {
      // 快取已過期，移除並返回 null
      this.cache.delete(url);
      return null;
    }

    // 返回快取的回應
    return entry.response;
  }

  /**
   * 將回應添加到快取
   */
  private addToCache(url: string, response: HttpResponse<any>, expiresIn: number = this.defaultCacheTime): void {
    // 檢查回應是否可快取
    if (!this.isResponseCacheable(response)) {
      return;
    }

    // 檢查快取大小，如果達到最大值，清理部分快取
    if (this.cache.size >= this.maxCacheSize) {
      this.cleanOldestCache();
    }

    // 創建快取項目
    const cacheEntry: CacheEntry = {
      url,
      response,
      timestamp: Date.now(),
      expiresIn
    };

    // 添加到快取
    this.cache.set(url, cacheEntry);
  }

  /**
   * 判斷回應是否可快取
   */
  private isResponseCacheable(response: HttpResponse<any>): boolean {
    // 檢查回應狀態碼
    if (response.status !== 200) {
      return false;
    }

    // 檢查回應標頭是否包含不快取的指示
    const cacheControl = response.headers.get('Cache-Control');
    if (cacheControl && (
        cacheControl.includes('no-store') ||
        cacheControl.includes('no-cache') ||
        cacheControl.includes('private'))) {
      return false;
    }

    return true;
  }

  /**
   * 清理過期的快取項目
   */
  private cleanExpiredCache(): void {
    const now = Date.now();

    // 只有在距離上次清理超過清理間隔時才執行
    if (now - this.lastCleanup < this.cleanupInterval) {
      return;
    }

    this.lastCleanup = now;

    // 遍歷所有快取項目，移除過期的項目
    this.cache.forEach((entry, url) => {
      if (now > entry.timestamp + entry.expiresIn) {
        this.cache.delete(url);
      }
    });
  }

  /**
   * 清理最舊的快取項目
   * 當快取達到最大大小時，移除 20% 最舊的項目
   */
  private cleanOldestCache(): void {
    // 將快取項目轉換為陣列並按時間戳排序
    const entries = Array.from(this.cache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);

    // 計算要移除的項目數量 (20% 的快取大小)
    const removeCount = Math.ceil(this.cache.size * 0.2);

    // 移除最舊的項目
    for (let i = 0; i < removeCount && i < entries.length; i++) {
      this.cache.delete(entries[i][0]);
    }
  }

  /**
   * 手動清理所有快取
   * 可在需要時從外部呼叫此方法
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * 手動清理特定 URL 的快取
   * 可在需要時從外部呼叫此方法
   */
  public clearCacheForUrl(url: string): void {
    this.cache.delete(url);
  }

  /**
   * 獲取當前快取大小
   */
  public getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * 設置最大快取大小
   */
  public setMaxCacheSize(size: number): void {
    this.maxCacheSize = size;

    // 如果當前快取大小超過新的最大值，清理多餘的項目
    if (this.cache.size > this.maxCacheSize) {
      this.cleanOldestCache();
    }
  }

  /**
   * 設置預設快取過期時間
   */
  public setDefaultCacheTime(timeInMs: number): void {
    this.defaultCacheTime = timeInMs;
  }
}
